// fleet-generated: do not edit by hand
//
// Rendered from    : infra/fleet/artifacts/shortcuts/persistence.tmpl.ts
// Parameters       : infra/fleet/registry/repos.ts  (entry "listto-app")
// Destination      : packages/fleet/shortcuts/src/persistence.ts
//
// TO CHANGE THIS FILE, edit the template or the registry entry in infra and
// re-render. Editing it here is reverted by the next render, and the guard
// in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo listto-app
//     node fleet/fleet.ts check --repo listto-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * Persistence adapter for user overrides.
 *
 * Defaults to localStorage; the engine consumes the abstract interface so a
 * Tauri variant can be swapped in without touching the registry.
 */

import type { PersistenceAdapter, UserOverride } from "./types";

/**
 * Exported so tests address the real key instead of guessing it. The governed
 * test used to hardcode a literal scope prefix, which in the other 23 repos
 * wrote to a key the adapter never reads -- the corruption and invalid-schema
 * cases passed everywhere by writing nowhere.
 */
export const STORAGE_KEY = "listto:shortcuts:v1";

/**
 * The key this product used BEFORE the engine was governed, or `null` when it
 * always used the one above.
 *
 * Four products need it. Two carried another product's scope by copy-paste
 * and two simply spelled their own differently from the derived scope. Governing the
 * file rewrote all four to the derived key, and without this fallback every one
 * of their users would have silently lost their customised shortcuts: the old
 * value is still in localStorage, and nothing would ever read it again.
 *
 * Read-only and one-way: the first successful read migrates the value forward
 * and drops the old key. The legacy key is never written to.
 */
export const LEGACY_STORAGE_KEY: string | null = null;

interface StorageLike {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

function getStorage(): StorageLike | null {
  try {
    if (typeof localStorage !== "undefined") {
      return localStorage;
    }
  } catch {
    /* sandboxed contexts may throw on access */
  }
  return null;
}

function isOverride(item: unknown): item is UserOverride {
  if (item === null || typeof item !== "object") {
    return false;
  }
  const command: unknown = Reflect.get(item, "command");
  const keys: unknown = Reflect.get(item, "keys");
  return typeof command === "string" && typeof keys === "string";
}

function isOverrideArray(value: unknown): value is UserOverride[] {
  return Array.isArray(value) && value.every(isOverride);
}

function loadFromStorage(): UserOverride[] {
  const storage = getStorage();
  if (!storage) {
    return [];
  }
  const raw = storage.getItem(STORAGE_KEY) ?? readLegacy(storage);
  if (!raw) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return isOverrideArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * One-time migration from the pre-governance key. Returns the raw value and
 * moves it under the current key, so this runs once per device and never again.
 */
function readLegacy(storage: StorageLike): string | null {
  if (LEGACY_STORAGE_KEY === null) {
    return null;
  }
  const raw = storage.getItem(LEGACY_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  storage.setItem(STORAGE_KEY, raw);
  storage.removeItem(LEGACY_STORAGE_KEY);
  return raw;
}

function saveToStorage(overrides: readonly UserOverride[]): void {
  const storage = getStorage();
  if (!storage) {
    return;
  }
  if (overrides.length === 0) {
    storage.removeItem(STORAGE_KEY);
    return;
  }
  storage.setItem(STORAGE_KEY, JSON.stringify(overrides));
}

// We declare these as `async` even though they wrap synchronous storage APIs:
// it satisfies promise-function-async (the interface returns Promise), and a
// future Tauri adapter implementing the same interface will be
// genuinely async.
/* oxlint-disable typescript-eslint/require-await */
export const localStoragePersistence: PersistenceAdapter = {
  async load() {
    return loadFromStorage();
  },
  async save(overrides) {
    saveToStorage(overrides);
  },
};

/** @public */
export const inMemoryPersistence = (): PersistenceAdapter => {
  let state: UserOverride[] = [];
  return {
    async load() {
      return state;
    },
    async save(overrides) {
      state = [...overrides];
    },
  };
};
/* oxlint-enable typescript-eslint/require-await */
